/**
 * DmsMixin — shared logic for all 4 DMS components:
 * DmsUploader, Drive, PortalDmsAdminTab, PortalDocuments
 *
 * Each component must implement dmsConfig() returning:
 * {
 *   apiBase: string,              // e.g. 'drive', 'dms/company/123'
 *   breadcrumbRootLabel: string,  // e.g. 'Home', 'Documenti'
 *   viewModeStorageKey: string,   // localStorage key for viewMode persistence
 *   hasRecentActivity: boolean,   // whether $refs.recentActivity exists
 *   defaultViewMode: string       // 'list' or 'grid' (default: 'list')
 * }
 */
export default {
	data() {
		var config = this.dmsConfig()
		return {
			dragItem: null,
			dragOverFolder: null,
			searchScope: 'local',
			searchResults: [],
			searchDebounce: null,
			loading: false,
			saving: false,
			uploading: false,
			uploadProgress: 0,
			isDragging: false,
			dragCount: 0,
			viewMode: localStorage.getItem(config.viewModeStorageKey) || config.defaultViewMode || 'list',
			search: '',
			storage: { total: 0 },
			folders: [],
			documents: [],
			breadcrumb: [],
			allTags: [],
			currentFolderId: null,
			selectedDoc: null,
			detailDrawer: false,
			docVersions: [],
			newFolderDialog: false,
			newFolderName: '',
			renameDialog: false,
			renameName: '',
			tagDialog: false,
			selectedTags: [],
			moveDialog: false,
			moveFolderTarget: null,
			folderTree: [],
			deleteDialog: false,
			previewDialog: false,
			previewDoc: null,
			previewUrl: null,
			contextMenu: { show: false, x: 0, y: 0, item: null, type: null },
			snackbar: { value: false, text: '', color: '' },
			selectedDocIds: [],
			selectedFolderIds: [],
			marquee: { active: false, startX: 0, startY: 0, x: 0, y: 0, w: 0, h: 0 },
			marqueeMoved: false,
			bulkMoveDialog: false,
			bulkMoveFolderTarget: null,
			bulkDeleteDialog: false,
			bulkLoading: false
		}
	},

	computed: {
		apiBase() {
			return this.dmsConfig().apiBase
		},
		hasSelection() {
			return this.selectedDocIds.length + this.selectedFolderIds.length > 0
		},
		selectionCount() {
			return this.selectedDocIds.length + this.selectedFolderIds.length
		},
		breadcrumbItems() {
			var label = this.dmsConfig().breadcrumbRootLabel || 'Home'
			var items = [{ text: label, folderId: null, disabled: false }]
			if (this.breadcrumb && this.breadcrumb.length) {
				this.breadcrumb.forEach((b, i) => {
					items.push({ text: b.name, folderId: b.id, disabled: i === this.breadcrumb.length - 1 })
				})
			}
			return items
		},
		filteredFolders() {
			var items = this.folders
			if (this.search) {
				var s = this.search.toLowerCase()
				items = items.filter(f => f.name.toLowerCase().includes(s))
			}
			return items
		},
		filteredDocuments() {
			if (this.search && this.searchScope === 'global') {
				return this.searchResults
			}
			var items = this.documents
			if (this.search) {
				var s = this.search.toLowerCase()
				items = items.filter(d =>
					d.name.toLowerCase().includes(s) ||
					(d.tags && d.tags.some(t => t.name.toLowerCase().includes(s)))
				)
			}
			return items
		},
		isGlobalSearch() {
			return this.search && this.searchScope === 'global'
		}
	},

	watch: {
		search(val) {
			if (val && this.searchScope === 'global') {
				this.debouncedGlobalSearch()
			}
			if (!val) {
				this.searchScope = 'local'
				this.searchResults = []
			}
		},
		searchScope(val) {
			if (val === 'global' && this.search) {
				this.globalSearch()
			}
			if (val === 'local') {
				this.searchResults = []
			}
		},
		viewMode(val) {
			localStorage.setItem(this.dmsConfig().viewModeStorageKey, val)
		}
	},

	created() {
		this.loadContent()
		this.loadTags()
	},

	beforeDestroy() {
		document.removeEventListener('mousemove', this._marqueeMoveHandler)
		document.removeEventListener('mouseup', this._marqueeUpHandler)
		document.removeEventListener('keydown', this._escHandler)
		document.body.style.userSelect = ''
	},

	methods: {
		// --- Data loading ---
		async loadContent() {
			this.loading = true
			try {
				var resp = await this.axios.get(this.apiBase + '/folders', {
					params: { parent_id: this.currentFolderId }
				})
				if (resp.data.success) {
					this.folders = resp.data.data.folders || []
					this.documents = resp.data.data.documents || []
					this.breadcrumb = resp.data.data.breadcrumb || []
					this.storage = resp.data.data.storage || { total: 0 }
				}
			} catch (e) {
				// silent
			} finally {
				this.loading = false
			}
		},

		async loadTags() {
			try {
				var resp = await this.axios.get(this.apiBase + '/tags')
				if (resp.data.success) {
					this.allTags = resp.data.data || []
				}
			} catch (e) {
				// silent
			}
		},

		// --- Navigation ---
		navigateToFolder(folderId) {
			this.currentFolderId = folderId
			this.loadContent()
		},

		toggleSelectDocument(doc) {
			if (this.selectedDoc && this.selectedDoc.id === doc.id) {
				this.selectedDoc = null
				this.detailDrawer = false
			} else {
				this.selectedDoc = doc
			}
		},

		openDocDetails(doc) {
			this.selectedDoc = doc
			this.detailDrawer = true
			this.loadVersions(doc.id)
		},

		debouncedOpenDocDetails(doc) {
			clearTimeout(this._clickTimer)
			this._clickTimer = setTimeout(() => {
				this.openDocDetails(doc)
			}, 250)
		},

		cancelDebouncedClick() {
			clearTimeout(this._clickTimer)
		},

		async loadVersions(docId) {
			try {
				var resp = await this.axios.get(this.apiBase + '/documents/' + docId + '/versions')
				if (resp.data.success) {
					this.docVersions = resp.data.data.versions || []
				}
			} catch (e) {
				this.docVersions = []
			}
		},

		// --- Download ---
		async downloadDoc(doc) {
			try {
				var response = await this.axios.get(this.apiBase + '/documents/' + doc.id + '/download', {
					responseType: 'blob',
					headers: { Accept: '*/*' }
				})
				var blob = new Blob([response.data], { type: response.headers['content-type'] })
				var url = window.URL.createObjectURL(blob)
				var link = document.createElement('a')
				link.href = url
				link.download = doc.name.endsWith('.' + doc.ext) ? doc.name : doc.name + '.' + doc.ext
				document.body.appendChild(link)
				link.click()
				document.body.removeChild(link)
				window.URL.revokeObjectURL(url)
			} catch (e) {
				this.showSnackbar('Errore nel download', 'error')
			}
		},

		async downloadFolder(folder) {
			try {
				var response = await this.axios.get(this.apiBase + '/folders/' + folder.id + '/download', {
					responseType: 'blob',
					headers: { Accept: '*/*' }
				})
				var blob = new Blob([response.data], { type: 'application/zip' })
				var url = window.URL.createObjectURL(blob)
				var link = document.createElement('a')
				link.href = url
				link.download = folder.name + '.zip'
				document.body.appendChild(link)
				link.click()
				document.body.removeChild(link)
				window.URL.revokeObjectURL(url)
			} catch (e) {
				this.showSnackbar('Errore nel download', 'error')
			}
		},

		// --- Upload & Drag ---
		handleFileUpload(e) {
			var files = e.target.files
			if (files.length) this.uploadFiles(Array.from(files))
			e.target.value = ''
		},

		onDragEnter() {
			if (this.dragItem) return
			this.dragCount++
			this.isDragging = true
		},

		onDragLeave() {
			this.dragCount--
			if (this.dragCount <= 0) {
				this.dragCount = 0
				this.isDragging = false
			}
		},

		handleDrop(e) {
			if (this.dragItem) return
			this.isDragging = false
			this.dragCount = 0

			var dt = e.dataTransfer
			var items = dt.items
			var hasEntryApi = items && items.length && items[0] && typeof items[0].webkitGetAsEntry === 'function'

			if (hasEntryApi) {
				// Raccoglie subito le entry (la lista viene svuotata dopo il primo await)
				var entries = []
				for (var i = 0; i < items.length; i++) {
					var entry = items[i].webkitGetAsEntry()
					if (entry) entries.push(entry)
				}
				var hasDir = entries.some(function (en) { return en && en.isDirectory })
				if (hasDir) {
					this.readAllEntries(entries).then(files => {
						if (files.length) this.uploadFilesWithPaths(files)
					})
					return
				}
			}

			var flat = dt.files
			if (flat.length) this.uploadFiles(Array.from(flat))
		},

		onItemDragStart(e, type, item) {
			var selectedDocs = this.selectedDocIds || []
			var selectedFolders = this.selectedFolderIds || []
			var inSelection = (type === 'doc' && selectedDocs.includes(item.id))
				|| (type === 'folder' && selectedFolders.includes(item.id))

			if (inSelection && (selectedDocs.length + selectedFolders.length > 1)) {
				this.dragItem = {
					type: 'bulk',
					id: item.id,
					name: item.name,
					docIds: selectedDocs.slice(),
					folderIds: selectedFolders.slice()
				}
			} else {
				this.dragItem = { type: type, id: item.id, name: item.name }
			}
			e.dataTransfer.effectAllowed = 'move'
			e.dataTransfer.setData('text/plain', JSON.stringify(this.dragItem))
		},

		onItemDragEnd() {
			this.dragItem = null
			this.dragOverFolder = null
		},

		onFolderDragOver(e, folder) {
			if (!this.dragItem) return
			if (this.dragItem.type === 'folder' && this.dragItem.id === folder.id) return
			if (this.dragItem.type === 'bulk' && (this.dragItem.folderIds || []).includes(folder.id)) return
			e.dataTransfer.dropEffect = 'move'
			this.dragOverFolder = folder.id
		},

		onFolderDragLeave() {
			this.dragOverFolder = null
		},

		async onFolderDrop(e, folder) {
			this.dragOverFolder = null
			if (!this.dragItem) return
			if (this.dragItem.type === 'folder' && this.dragItem.id === folder.id) return

			var item = this.dragItem
			this.dragItem = null

			try {
				if (item.type === 'bulk') {
					var docIds = (item.docIds || []).slice()
					var folderIds = (item.folderIds || []).filter(function (id) { return id !== folder.id })
					if (docIds.length) {
						await this.axios.post(this.apiBase + '/documents/move-bulk', { ids: docIds, folder_id: folder.id })
					}
					if (folderIds.length) {
						await this.axios.post(this.apiBase + '/folders/move-bulk', { ids: folderIds, parent_id: folder.id })
					}
					if (this.selectedDocIds) this.selectedDocIds = []
					if (this.selectedFolderIds) this.selectedFolderIds = []
					this.loadContent()
					this.showSnackbar((docIds.length + folderIds.length) + ' elementi spostati in ' + folder.name, 'success')
				} else if (item.type === 'folder') {
					await this.axios.put(this.apiBase + '/folders/' + item.id + '/move', { parent_id: folder.id })
					this.loadContent()
					this.showSnackbar(item.name + ' spostato in ' + folder.name, 'success')
				} else {
					await this.axios.put(this.apiBase + '/documents/' + item.id + '/move', { folder_id: folder.id })
					this.loadContent()
					this.showSnackbar(item.name + ' spostato in ' + folder.name, 'success')
				}
			} catch (err) {
				this.showSnackbar('Errore nello spostamento', 'error')
			}
		},

		async uploadFiles(files) {
			files = this.withoutSystemFiles(files)
			if (!files.length) return
			this.uploading = true
			this.uploadProgress = 0
			var total = files.length
			var processed = 0
			var failed = []

			for (var file of files) {
				try {
					await this.uploadSingleFile(file, this.currentFolderId || null)
				} catch (e) {
					failed.push({ name: file.name, error: e })
				}
				processed++
				this.uploadProgress = Math.round((processed / total) * 100)
			}

			this.finishUpload(total, failed)
		},

		// File che il sistema operativo mette nelle cartelle (miniature, impostazioni
		// della cartella, file e cartelle nascosti come .DS_Store o .git): l'utente non
		// li ha scelti e il backend li rifiuterebbe, facendo fallire in massa l'upload
		// di una cartella.
		isSystemFile(file) {
			var segments = this.getRelativePath(file).split('/')
			var name = segments[segments.length - 1].toLowerCase()
			if (['desktop.ini', 'thumbs.db', 'ehthumbs.db'].includes(name)) return true
			return segments.some(seg => seg.startsWith('.') || seg === '__MACOSX')
		},

		// Toglie i file di sistema; se non resta nulla lo dice, invece di non fare niente.
		withoutSystemFiles(files) {
			var uploadable = files.filter(f => !this.isSystemFile(f))
			if (files.length && !uploadable.length) {
				this.showSnackbar('Nessun file caricato: c\'erano solo file di sistema (es. desktop.ini, .DS_Store)', 'warning')
			}
			return uploadable
		},

		// Chiude un upload multiplo con un riepilogo veritiero: prima gli errori dei
		// singoli file venivano ignorati e il messaggio finale li contava come caricati.
		finishUpload(total, failed) {
			this.uploading = false
			var ok = total - failed.length
			var uploaded = ok + (ok === 1 ? ' file caricato' : ' file caricati')
			if (!failed.length) {
				this.showSnackbar(uploaded, 'success')
			} else {
				var details = failed.slice(0, 5).map(f => f.name + ' (' + this.getErrorMessage(f.error) + ')').join(', ')
				if (failed.length > 5) details += ' e altri ' + (failed.length - 5)
				var notUploaded = failed.length + (failed.length === 1 ? ' non caricato' : ' non caricati')
				// Resta piu' a lungo del solito: l'elenco degli scarti va letto
				this.showSnackbar(uploaded + ', ' + notUploaded + ': ' + details, ok ? 'warning' : 'error', 15000)
			}
			this.loadContent()
			this.onAfterMutation()
		},

		// Carica un singolo file nella cartella indicata. Punto di estensione:
		// i componenti con endpoint diverso (es. PortalDocuments) sovrascrivono questo metodo.
		// silentValidation: i rifiuti li elenca finishUpload, senza una snackbar
		// globale per ogni file che coprirebbe il riepilogo.
		async uploadSingleFile(file, folderId) {
			var formData = new FormData()
			formData.append('file', file)
			if (folderId) formData.append('folder_id', folderId)
			await this.axios.post(this.apiBase + '/upload', formData, {
				headers: { 'Content-Type': 'multipart/form-data' },
				silentValidation: true
			})
		},

		// Selezione di una cartella dal PC (input con attributo webkitdirectory)
		handleFolderUpload(e) {
			var files = Array.from(e.target.files)
			if (files.length) {
				// Se il browser non espone i path relativi (webkitdirectory non supportato),
				// si degrada all'upload flat nella cartella corrente invece di produrre
				// risultati inattesi.
				var hasPaths = files.some(function (f) { return f.webkitRelativePath })
				if (hasPaths) {
					this.uploadFilesWithPaths(files)
				} else {
					this.uploadFiles(files)
				}
			}
			e.target.value = ''
		},

		// Carica una lista di file preservando la struttura di cartelle/sottocartelle:
		// crea le cartelle mancanti a partire dalla cartella corrente e carica ogni file
		// nella cartella risolta dal suo percorso relativo.
		async uploadFilesWithPaths(files) {
			files = this.withoutSystemFiles(files)
			if (!files.length) return
			this.uploading = true
			this.uploadProgress = 0
			var total = files.length
			var processed = 0
			var failed = []

			// Cache percorso-relativo -> folder_id, per non ricreare la stessa cartella
			var folderCache = {}
			folderCache[''] = this.currentFolderId || null

			for (var file of files) {
				var relPath = this.getRelativePath(file)
				var segments = relPath.split('/')
				segments.pop() // rimuove il nome del file, restano le cartelle

				var folderId = this.currentFolderId || null
				try {
					folderId = await this.ensureFolderPath(segments, folderCache)
				} catch (e) {
					folderId = this.currentFolderId || null
				}

				try {
					await this.uploadSingleFile(file, folderId)
				} catch (e) {
					failed.push({ name: relPath, error: e })
				}
				processed++
				this.uploadProgress = Math.round((processed / total) * 100)
			}

			this.finishUpload(total, failed)
		},

		// Ritorna (creando le cartelle mancanti) l'id della cartella corrispondente ai segmenti.
		async ensureFolderPath(segments, cache) {
			var parentId = cache['']
			var accum = []
			for (var seg of segments) {
				seg = (seg || '').trim()
				if (!seg) continue
				accum.push(seg)
				var key = accum.join('/')
				if (Object.prototype.hasOwnProperty.call(cache, key)) {
					parentId = cache[key]
					continue
				}
				try {
					var newId = await this.createFolderRemote(seg, parentId)
					cache[key] = newId
					parentId = newId
				} catch (e) {
					// Creazione fallita (nome non valido o errore rete): si degrada usando
					// l'ultimo antenato valido, memorizzandolo così i file fratelli non
					// ritentano la stessa creazione e la struttura già creata è preservata.
					cache[key] = parentId
					return parentId
				}
			}
			return parentId
		},

		// Crea una cartella sul backend e ritorna il suo id.
		async createFolderRemote(name, parentId) {
			var resp = await this.axios.post(this.apiBase + '/folders', {
				name: name,
				parent_id: parentId
			})
			if (resp.data && resp.data.success && resp.data.data) {
				return resp.data.data.id
			}
			return parentId
		},

		// Percorso relativo di un file: da webkitdirectory (webkitRelativePath)
		// oppure dal drag&drop di cartelle (relativePathOverride impostato da readAllEntries).
		getRelativePath(file) {
			return file.webkitRelativePath || file.relativePathOverride || file.name
		},

		// Legge ricorsivamente le FileSystemEntry di un drop, restituendo i File
		// con relativePathOverride valorizzato al percorso completo (cartella/sotto/file).
		readAllEntries(entries) {
			var files = []

			function readEntry(entry, prefix) {
				return new Promise(function (resolve) {
					if (entry.isFile) {
						entry.file(function (file) {
							file.relativePathOverride = prefix + entry.name
							files.push(file)
							resolve()
						}, function () { resolve() })
					} else if (entry.isDirectory) {
						var reader = entry.createReader()
						var collected = []
						var readBatch = function () {
							reader.readEntries(function (batch) {
								if (!batch.length) {
									Promise.all(collected.map(function (child) {
										return readEntry(child, prefix + entry.name + '/')
									})).then(resolve)
								} else {
									collected = collected.concat(Array.from(batch))
									readBatch()
								}
							}, function () { resolve() })
						}
						readBatch()
					} else {
						resolve()
					}
				})
			}

			return Promise.all(entries.map(function (entry) {
				return readEntry(entry, '')
			})).then(function () { return files })
		},

		async handleVersionUpload(e) {
			var file = e.target.files[0]
			if (!file || !this.selectedDoc) return

			var formData = new FormData()
			formData.append('file', file)

			try {
				await this.axios.post(this.apiBase + '/documents/' + this.selectedDoc.id + '/new-version', formData, {
					headers: { 'Content-Type': 'multipart/form-data' }
				})
				this.showSnackbar('Nuova versione caricata', 'success')
				this.loadContent()
				this.loadVersions(this.selectedDoc.id)
			} catch (e) {
				this.showSnackbar(this.getErrorMessage(e, 'Errore nel caricamento'), 'error')
			}
			e.target.value = ''
		},

		// --- Folder CRUD ---
		async createFolder() {
			if (!this.newFolderName.trim()) return
			this.saving = true
			try {
				await this.axios.post(this.apiBase + '/folders', {
					name: this.newFolderName.trim(),
					parent_id: this.currentFolderId
				})
				this.newFolderDialog = false
				this.newFolderName = ''
				this.loadContent()
				this.onAfterMutation()
			} catch (e) {
				this.showSnackbar(this.getErrorMessage(e, 'Errore nella creazione'), 'error')
			} finally {
				this.saving = false
			}
		},

		// --- Context menus ---
		showFolderMenu(e, folder) {
			this.contextMenu = { show: true, x: e.clientX, y: e.clientY, item: folder, type: 'folder' }
		},

		showDocMenu(e, doc) {
			this.contextMenu = { show: true, x: e.clientX, y: e.clientY, item: doc, type: 'doc' }
		},

		// --- Rename ---
		openRenameDialog() {
			this.renameName = this.contextMenu.item.name
			this.renameDialog = true
		},

		async renameItem() {
			if (!this.renameName.trim()) return
			if (/[\\/:*?"<>|]/.test(this.renameName)) {
				this.showSnackbar('Il nome non può contenere i caratteri \\ / : * ? " < > |', 'error')
				return
			}
			this.saving = true
			var item = this.contextMenu.item
			var type = this.contextMenu.type

			try {
				if (type === 'folder') {
					await this.axios.put(this.apiBase + '/folders/' + item.id, { name: this.renameName.trim() })
				} else {
					await this.axios.put(this.apiBase + '/documents/' + item.id, { name: this.renameName.trim() })
				}
				this.renameDialog = false
				this.loadContent()
				this.onAfterMutation()
			} catch (e) {
				this.showSnackbar('Errore', 'error')
			} finally {
				this.saving = false
			}
		},

		// --- Tags ---
		openTagDialog() {
			this.selectedTags = (this.contextMenu.item.tags || []).map(t => t)
			this.tagDialog = true
		},

		async saveTags() {
			this.saving = true
			var doc = this.contextMenu.item

			// Cattura testo non confermato nel combobox
			var combobox = this.$refs.tagCombobox
			if (combobox && combobox.lazySearch && combobox.lazySearch.trim()) {
				this.selectedTags.push(combobox.lazySearch.trim())
				combobox.lazySearch = ''
			}

			var tagIds = this.selectedTags.map(t => typeof t === 'object' ? t.id : t).filter(Boolean)

			try {
				await this.axios.post(this.apiBase + '/documents/' + doc.id + '/tags', { tag_ids: tagIds })
				this.tagDialog = false
				this.loadTags()
				this.loadContent()
			} catch (e) {
				this.showSnackbar('Errore', 'error')
			} finally {
				this.saving = false
			}
		},

		// --- Move ---
		openMoveDialog() {
			this.moveFolderTarget = null
			this.loadFolderTree()
			this.moveDialog = true
		},

		async loadFolderTree() {
			try {
				var resp = await this.axios.get(this.apiBase + '/folders', { params: { tree: true } })
				if (resp.data.success) {
					this.folderTree = this.buildTree(resp.data.data.folders || [])
				}
			} catch (e) {
				this.folderTree = []
			}
		},

		buildTree(folders) {
			var map = {}
			var roots = []
			folders.forEach(f => { map[f.id] = { id: f.id, name: f.name, children: [] } })
			folders.forEach(f => {
				if (f.parent_id && map[f.parent_id]) {
					map[f.parent_id].children.push(map[f.id])
				} else {
					roots.push(map[f.id])
				}
			})
			return roots
		},

		async moveItem() {
			this.saving = true
			var item = this.contextMenu.item
			var type = this.contextMenu.type

			try {
				if (type === 'folder') {
					await this.axios.put(this.apiBase + '/folders/' + item.id + '/move', { parent_id: this.moveFolderTarget })
				} else {
					await this.axios.put(this.apiBase + '/documents/' + item.id + '/move', { folder_id: this.moveFolderTarget })
				}
				this.moveDialog = false
				this.loadContent()
				this.onAfterMutation()
			} catch (e) {
				this.showSnackbar('Errore nello spostamento', 'error')
			} finally {
				this.saving = false
			}
		},

		// --- Delete ---
		confirmDelete() {
			this.deleteDialog = true
		},

		async deleteItem() {
			this.saving = true
			var item = this.contextMenu.item
			var type = this.contextMenu.type

			try {
				if (type === 'folder') {
					await this.axios.post(this.apiBase + '/folders/delete', { id: item.id })
				} else {
					await this.axios.post(this.apiBase + '/documents/delete', { id: item.id })
				}
				this.deleteDialog = false
				this.showSnackbar('Eliminato con successo', 'success')
				this.loadContent()
				this.onAfterMutation()
			} catch (e) {
				this.showSnackbar('Errore', 'error')
			} finally {
				this.saving = false
			}
		},

		// --- Search ---
		debouncedGlobalSearch() {
			clearTimeout(this.searchDebounce)
			this.searchDebounce = setTimeout(() => {
				this.globalSearch()
			}, 400)
		},

		async globalSearch() {
			if (!this.search) return
			this.loading = true
			try {
				var resp = await this.axios.get(this.apiBase + '/documents/search', {
					params: { q: this.search }
				})
				if (resp.data.success) {
					this.searchResults = resp.data.data || []
				}
			} catch (e) {
				this.searchResults = []
			}
			this.loading = false
		},

		// --- Preview ---
		isPreviewable(doc) {
			var exts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'mp4', 'webm', 'pdf']
			// Tollera l'assenza dell'elemento come canModify(): il menu contestuale la
			// interroga anche quando non e' stato ancora selezionato nulla.
			return exts.includes(((doc && doc.ext) || '').toLowerCase())
		},

		openPreview(doc) {
			this.previewDoc = doc
			this.previewUrl = this.apiBase + '/documents/' + doc.id + '/download'
			this.previewDialog = true
		},

		closePreview() {
			this.previewDialog = false
			this.previewUrl = null
			this.previewDoc = null
		},

		// --- Helpers ---
		getFileIcon(ext) {
			var map = {
				pdf: 'mdi-file-pdf-box', doc: 'mdi-file-word-box', docx: 'mdi-file-word-box',
				xls: 'mdi-file-excel-box', xlsx: 'mdi-file-excel-box',
				ppt: 'mdi-file-powerpoint-box', pptx: 'mdi-file-powerpoint-box',
				jpg: 'mdi-file-image', jpeg: 'mdi-file-image', png: 'mdi-file-image', gif: 'mdi-file-image', webp: 'mdi-file-image',
				zip: 'mdi-folder-zip', rar: 'mdi-folder-zip', '7z': 'mdi-folder-zip',
				txt: 'mdi-file-document', csv: 'mdi-file-delimited',
				xml: 'mdi-file-code', json: 'mdi-file-code',
				p7m: 'mdi-file-certificate', pfx: 'mdi-file-certificate',
				ovpn: 'mdi-folder-network'
			}
			return map[ext] || 'mdi-file'
		},

		getFileColor(ext) {
			var map = {
				pdf: 'red', doc: 'blue', docx: 'blue',
				xls: 'green', xlsx: 'green',
				ppt: 'orange', pptx: 'orange',
				jpg: 'purple', jpeg: 'purple', png: 'purple', gif: 'purple',
				zip: 'amber', p7m: 'teal'
			}
			return map[ext] || 'grey'
		},

		formatSize(bytes) {
			if (!bytes) return '0 B'
			var units = ['B', 'KB', 'MB', 'GB']
			var i = 0
			var size = Number(bytes)
			while (size >= 1024 && i < units.length - 1) { size /= 1024; i++ }
			return size.toFixed(i > 0 ? 1 : 0) + ' ' + units[i]
		},

		// Come l'interceptor globale: per un 422 il primo errore di validazione,
		// non il generico "The given data was invalid." di Laravel
		getErrorMessage(e, fallback) {
			var data = e && e.response && e.response.data
			if (data && data.errors) {
				var firstField = Object.values(data.errors)[0]
				if (Array.isArray(firstField) && firstField.length) return firstField[0]
			}
			if (data && data.message) return data.message
			return fallback || 'Errore'
		},

		showSnackbar(text, color, timeout) {
			this.snackbar = { value: true, text, color, timeout }
		},

		onAfterMutation() {
			if (this.dmsConfig().hasRecentActivity && this.$refs.recentActivity) {
				this.$refs.recentActivity.reload()
			}
		},

		// --- Marquee selection ---
		onMarqueeMouseDown(e) {
			if (this.viewMode !== 'grid') return
			if (e.target.closest('.v-card[draggable="true"]')) return
			if (this.uploading) return

			var zone = this.$refs.dropZone
			if (!zone) return
			var rect = zone.getBoundingClientRect()
			var x = e.clientX - rect.left + zone.scrollLeft
			var y = e.clientY - rect.top + zone.scrollTop

			this.marquee = { active: true, startX: x, startY: y, x: x, y: y, w: 0, h: 0 }
			this.marqueeMoved = false

			this._cardBoxes = this.computeCardBoxes()
			this._rafPending = false
			this._lastSelectedDocs = []
			this._lastSelectedFolders = []

			this._marqueeMoveHandler = this.onMarqueeMouseMove
			this._marqueeUpHandler = this.onMarqueeMouseUp
			this._escHandler = this.onEscKey
			this._scrollHandler = this.onMarqueeScroll
			document.addEventListener('mousemove', this._marqueeMoveHandler)
			document.addEventListener('mouseup', this._marqueeUpHandler)
			document.addEventListener('keydown', this._escHandler)
			zone.addEventListener('scroll', this._scrollHandler)

			document.body.style.userSelect = 'none'
		},

		computeCardBoxes() {
			var zone = this.$refs.dropZone
			if (!zone) return { docs: [], folders: [] }
			var zoneRect = zone.getBoundingClientRect()
			var sl = zone.scrollLeft
			var st = zone.scrollTop
			var docs = []
			var docCards = zone.querySelectorAll('[data-doc-id]')
			for (var i = 0; i < docCards.length; i++) {
				var card = docCards[i]
				var cr = card.getBoundingClientRect()
				var left = cr.left - zoneRect.left + sl
				var top = cr.top - zoneRect.top + st
				docs.push({
					id: parseInt(card.getAttribute('data-doc-id')),
					left: left,
					top: top,
					right: left + cr.width,
					bottom: top + cr.height
				})
			}
			var folders = []
			var folderCards = zone.querySelectorAll('[data-folder-id]')
			for (var j = 0; j < folderCards.length; j++) {
				var fc = folderCards[j]
				var fr = fc.getBoundingClientRect()
				var fleft = fr.left - zoneRect.left + sl
				var ftop = fr.top - zoneRect.top + st
				folders.push({
					id: parseInt(fc.getAttribute('data-folder-id')),
					left: fleft,
					top: ftop,
					right: fleft + fr.width,
					bottom: ftop + fr.height
				})
			}
			return { docs: docs, folders: folders }
		},

		onMarqueeScroll() {
			this._cardBoxes = this.computeCardBoxes()
		},

		onMarqueeMouseMove(e) {
			if (!this.marquee.active) return
			var zone = this.$refs.dropZone
			if (!zone) return
			var rect = zone.getBoundingClientRect()
			var x = e.clientX - rect.left + zone.scrollLeft
			var y = e.clientY - rect.top + zone.scrollTop

			var sx = this.marquee.startX
			var sy = this.marquee.startY
			var newX = Math.min(sx, x)
			var newY = Math.min(sy, y)
			var newW = Math.abs(x - sx)
			var newH = Math.abs(y - sy)

			this.marquee = Object.assign({}, this.marquee, { x: newX, y: newY, w: newW, h: newH })

			if (newW > 4 || newH > 4) this.marqueeMoved = true

			if (!this._rafPending) {
				this._rafPending = true
				var self = this
				requestAnimationFrame(function () {
					self._rafPending = false
					self.recomputeMarqueeSelection()
				})
			}
		},

		onMarqueeMouseUp() {
			if (!this.marquee.active) return
			if (!this.marqueeMoved) {
				this.clearSelection()
			}
			this.marquee = Object.assign({}, this.marquee, { active: false, w: 0, h: 0 })

			var zone = this.$refs.dropZone
			if (zone && this._scrollHandler) zone.removeEventListener('scroll', this._scrollHandler)
			document.removeEventListener('mousemove', this._marqueeMoveHandler)
			document.removeEventListener('mouseup', this._marqueeUpHandler)
			document.body.style.userSelect = ''

			this._cardBoxes = null
			this._rafPending = false
		},

		onEscKey(e) {
			if (e.key === 'Escape') {
				this.clearSelection()
				document.removeEventListener('keydown', this._escHandler)
			}
		},

		arraysEqual(a, b) {
			if (a.length !== b.length) return false
			for (var i = 0; i < a.length; i++) if (a[i] !== b[i]) return false
			return true
		},

		recomputeMarqueeSelection() {
			if (!this._cardBoxes) return
			var m = this.marquee
			var mLeft = m.x
			var mTop = m.y
			var mRight = m.x + m.w
			var mBottom = m.y + m.h

			var docIds = []
			var boxes = this._cardBoxes.docs
			for (var i = 0; i < boxes.length; i++) {
				var b = boxes[i]
				if (b.right >= mLeft && b.left <= mRight && b.bottom >= mTop && b.top <= mBottom) {
					docIds.push(b.id)
				}
			}
			var folderIds = []
			var fboxes = this._cardBoxes.folders
			for (var j = 0; j < fboxes.length; j++) {
				var fb = fboxes[j]
				if (fb.right >= mLeft && fb.left <= mRight && fb.bottom >= mTop && fb.top <= mBottom) {
					folderIds.push(fb.id)
				}
			}

			if (!this.arraysEqual(docIds, this._lastSelectedDocs)) {
				this.selectedDocIds = docIds
				this._lastSelectedDocs = docIds
			}
			if (!this.arraysEqual(folderIds, this._lastSelectedFolders)) {
				this.selectedFolderIds = folderIds
				this._lastSelectedFolders = folderIds
			}
		},

		clearSelection() {
			this.selectedDocIds = []
			this.selectedFolderIds = []
		},

		// --- Bulk actions ---
		openBulkMoveDialog() {
			this.bulkMoveFolderTarget = null
			this.bulkMoveDialog = true
		},

		async confirmBulkMove() {
			this.bulkLoading = true
			try {
				var targetId = this.bulkMoveFolderTarget
				if (this.selectedDocIds.length) {
					await this.axios.post(this.apiBase + '/documents/move-bulk', {
						ids: this.selectedDocIds,
						folder_id: targetId
					})
				}
				if (this.selectedFolderIds.length) {
					await this.axios.post(this.apiBase + '/folders/move-bulk', {
						ids: this.selectedFolderIds,
						parent_id: targetId
					})
				}
				this.bulkMoveDialog = false
				this.clearSelection()
				this.loadContent()
				this.showSnackbar('Elementi spostati', 'success')
			} catch (e) {
				this.showSnackbar('Errore nello spostamento', 'error')
			} finally {
				this.bulkLoading = false
			}
		},

		async confirmBulkDelete() {
			this.bulkLoading = true
			try {
				if (this.selectedDocIds.length) {
					await this.axios.post(this.apiBase + '/documents/delete-bulk', {
						ids: this.selectedDocIds
					})
				}
				if (this.selectedFolderIds.length) {
					await this.axios.post(this.apiBase + '/folders/delete-bulk', {
						ids: this.selectedFolderIds
					})
				}
				this.bulkDeleteDialog = false
				this.clearSelection()
				this.loadContent()
				this.showSnackbar('Elementi eliminati', 'success')
			} catch (e) {
				this.showSnackbar('Errore nell\'eliminazione', 'error')
			} finally {
				this.bulkLoading = false
			}
		},

		async bulkDownload() {
			this.bulkLoading = true
			try {
				var resp = await this.axios.post(this.apiBase + '/documents/download-bulk', {
					document_ids: this.selectedDocIds,
					folder_ids: this.selectedFolderIds
				}, {
					responseType: 'blob',
					headers: { Accept: '*/*' }
				})
				var blob = new Blob([resp.data], { type: 'application/zip' })
				var url = window.URL.createObjectURL(blob)
				var link = document.createElement('a')
				link.href = url
				link.download = 'dms-export.zip'
				document.body.appendChild(link)
				link.click()
				document.body.removeChild(link)
				window.URL.revokeObjectURL(url)
			} catch (e) {
				this.showSnackbar('Errore nel download', 'error')
			} finally {
				this.bulkLoading = false
			}
		}
	}
}
